import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { LOGO_URL, mockData } from "../mock";

const NAV = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Founder", path: "/founders" },
  { label: "Services", path: "/services" },
  { label: "Portfolio", path: "/portfolio" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background/90 backdrop-blur-xl border-border"
          : "bg-background/70 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt="Robo Demo LLC Logo"
              className="h-14 w-14 object-contain"
            />
            <div className="leading-none">
              <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                Robo Demo<span className="text-primary"> LLC</span>
              </h1>
              <p className="label-kicker text-[10px] text-muted-foreground tracking-[0.3em] mt-1">
                Veteran Owned
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`relative text-sm font-semibold uppercase tracking-wide transition-colors duration-200 ${
                  isActive(item.path)
                    ? "text-primary"
                    : "text-gray-300 hover:text-white"
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-primary" />
                )}
              </Link>
            ))}
            <a
              href={`tel:${mockData.contact.phone}`}
              data-testid="header-phone"
              className="flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4 text-primary" />
              {mockData.contact.phone}
            </a>
            <Button
              data-testid="header-get-quote-btn"
              onClick={() => navigate("/quote")}
              className="rounded-none font-bold uppercase tracking-widest"
            >
              Get Quote
            </Button>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              data-testid="mobile-menu-toggle"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border py-4 space-y-1" data-testid="mobile-nav">
            {NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`block px-3 py-3 text-base font-semibold uppercase tracking-wide ${
                  isActive(item.path)
                    ? "text-primary border-l-2 border-primary bg-white/5"
                    : "text-gray-300 hover:text-white hover:bg-white/5"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="px-3 pt-3">
              <Button
                data-testid="mobile-get-quote-btn"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate("/quote");
                }}
                className="w-full rounded-none font-bold uppercase tracking-widest"
              >
                Get Quote
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
